import { Helmet } from 'react-helmet-async';

import { Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Tabs,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import useDebounce from '../../hooks/useDebounce';
import { listTrainingVideos } from '../../services/apis/InstallerAPIs';
import StyledInput from '../../components/inputs/StyledInput';
import TrainingVideoList from '../../components/ResourcesPage/TrainingVideoList';

type Tabs = 'Borrowers' | 'Installers';

const OnePagersView: FC<Record<string, never>> = () => {
  const trainingVideosQuery = useQuery('trainingVideos', listTrainingVideos);
  const {
    data: resources,
    isLoading,
    isError,
    error,
    refetch,
  } = trainingVideosQuery;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<Tabs>('Installers');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newQuery = event.target.value;
    setSearchQuery(newQuery);
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 200);

  const filteredResources =
    resources?.filter((resource) => {
      const searchableFields = [resource.title, resource.description];
      return searchableFields.some((field) =>
        field?.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
    }) || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography variant="body1" color="error">
          An error occurred when fetching resources:{' '}
          {(error &&
            typeof error === 'object' &&
            'message' in error &&
            error?.message) ??
            'Unknown error'}
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Button variant="outlined" onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <div>
      <Grid container spacing={2} sx={{ mt: 2.5 }}>
        <Grid item xs={12} sm={7} spacing={1}>
          <Typography variant="body1" color="text.secondary">
            Welcome to the Sunstone Credit Training Center
            <br />
            Here, you&apos;ll find quick and easy video tutorials to help you
            navigate the Sunstone Credit portal and make the most of our
            commercial solar loan platform. Whether you&apos;re pricing and
            submitting a new project, tracking a submitted loan application, or
            working to get an approved project funded, these videos will walk
            you through each step.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={5}>
          <Box display={'flex'} justifyContent="flex-end">
            <StyledInput
              placeholder="Search videos"
              variant="outlined"
              value={searchQuery}
              fullWidth={true}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
                'aria-label': 'Search documents',
                role: 'search',
              }}
              onChange={handleSearchChange}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ display: { xs: 'block', sm: 'none' } }}>
        <Grid item xs={12}>
          <Typography
            style={{ fontSize: '1rem', fontWeight: '500' }}
            sx={{ pt: 3 }}
          >
            Helpful documents to share with borrowers:
          </Typography>
        </Grid>
      </Grid>
      <TrainingVideoList videos={filteredResources} search={searchQuery} />
    </div>
  );
};

const TrainingVideoPage: FC<Record<string, never>> = () => {
  return (
    <>
      <Helmet>
        <title>Training Videos</title>
      </Helmet>

      <div
        style={{
          backgroundColor: 'white',
          padding: '1rem',
          paddingTop: '2rem;',
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
        }}
      >
        <OnePagersView />
      </div>
    </>
  );
};

export default TrainingVideoPage;
